/**
 * This program is free software; you can redistribute it and/or
 * modify it under the terms of the GNU General Public License
 * as published by the Free Software Foundation; under version 2
 * of the License (non-upgradable).
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program; if not, write to the Free Software
 * Foundation, Inc., 51 Franklin Street, Fifth Floor, Boston, MA  02110-1301, USA.
 *
 * Copyright (c) 2021-2023 (original work) Open Assessment Technologies SA ;
 */
import { __ } from '../i18n/language.js';

/**
 * Humanize a duration in seconds to a human readable and localized string
 * @param {number} durationSecs - the duration in seconds
 * @returns {string} the humanized duration
 */
export function humanizeTime(durationSecs) {
    if (typeof durationSecs !== 'number' || Number.isNaN(durationSecs)) {
        return '';
    }
    let [hours, mins, secs] = getHoursMinutesSeconds(durationSecs);
    const days = Math.trunc(hours / 24);
    hours = Math.trunc(hours % 24);
    const humanized = [];

    if (days > 0) {
        humanized.push(days === 1 ? __('1 day') : __('%d days', days));
    }
    if (hours > 0) {
        humanized.push(hours === 1 ? __('1 hour') : __('%d hours', hours));
    }
    if (mins > 0) {
        humanized.push(mins === 1 ? __('1 minute') : __('%d minutes', mins));
    }
    if (secs > 0 || (days === 0 && hours === 0 && mins === 0)) {
        humanized.push(secs === 1 ? __('%d second', secs) : __('%d seconds', secs));
    }
    return humanized.join(' ');
}

/**
 * Timer duration to visual string.
 * @param {number} durationMs - duration in milliseconds
 * @param {object} throttleConfig - config to throttle (update less frequently) the duration - @see getThrottledDurationParts
 * @returns {string}
 */
export function humanizeDurationVisual(durationMs, throttleConfig) {
    let [hours, minutes, seconds] = getThrottledDurationParts(durationMs, throttleConfig);
    const days = Math.trunc(hours / 24);
    hours = Math.trunc(hours % 24);
    let result = [];

    if (days) {
        result.push(__('%dd', days));
    }
    if (hours) {
        result.push(__('%dh', hours));
    }
    if (minutes) {
        result.push(__('%dmin', minutes));
    }
    if (seconds || (!days && !hours && !minutes && !seconds)) {
        result.push(__('%ds', seconds));
    }
    return result.join(' ');
}

/**
 * Timer duration to screenreader-readable string.
 * @param {number} durationMs - duration in milliseconds
 * @param {object} throttleConfig - config to throttle (update less frequently) the duration - @see getThrottledDurationParts
 * @returns {string}
 */
export function humanizeDurationSpeakable(durationMs, throttleConfig) {
    const [hours, minutes, seconds] = getThrottledDurationParts(durationMs, throttleConfig);
    return humanizeTime(hours * 60 * 60 + minutes * 60 + seconds);
}

/**
 * Extract hours/minutes/seconds from total amount of milliseconds
 * and throttle them according to timer duration rules
 * @param {number} durationMs - duration in milliseconds
 * @param {object} throttleConfig - config to throttle (update less frequently) the duration
 * @param {number} [throttleConfig.minutesThreshold=0] if > 0, then for duration minutes above this threshold, duration will be truncated to the nearest minute
 *                                                              and below threshold, not truncated
 * @returns {Array<Number>} [hours, minutes, seconds]
 */
export function getThrottledDurationParts(durationMs, { minutesThreshold } = { minutesThreshold: 0 }) {
    if (typeof durationMs !== 'number' || Number.isNaN(durationMs)) {
        throw new TypeError('A duration is a number in milliseconds');
    }

    const [hours, minutes, seconds] = getHoursMinutesSeconds(Math.max(durationMs, 0) / 1000);

    const totalMinutes = (hours * 60) + minutes;
    const isBelowMinutesThreshold = !minutesThreshold || totalMinutes < minutesThreshold;

    if (!isBelowMinutesThreshold || hours >= 24) {
        return [hours, minutes, 0];
    }
    return [hours, minutes, seconds];
}

/**
 * Extract hours/minutes/seconds from total amount of seconds
 * (if total amount of seconds is > 1 day, hours will be > 24 - total amount)
 * @param {Number} totalSeconds
 * @returns {Array<Number>} [hours, minutes, seconds]
 */
function getHoursMinutesSeconds(totalSeconds) {
    const hours = Math.trunc(totalSeconds / 60 / 60);
    const minutes = Math.trunc((totalSeconds / 60) % 60);
    const seconds = Math.trunc(totalSeconds % 60);
    return [hours, minutes, seconds];
}
